import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Search, Trash2,
    ChevronDown, ChevronUp, ChevronsUpDown,
    ChevronLeft, ChevronRight, Loader2, AlertCircle,
    AlertTriangle, X, CheckCircle2
} from 'lucide-react';

const LoginLogsTab = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Пагинация
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Сортировка
    const [sortBy, setSortBy] = useState('LoginTime');
    const [sortDescending, setSortDescending] = useState(true);

    // Поиск
    const [searchQuery, setSearchQuery] = useState('');

    // Модальное окно очистки
    const [isCleanupOpen, setIsCleanupOpen] = useState(false);
    const [isCleanupLoading, setIsCleanupLoading] = useState(false);
    const [cleanupError, setCleanupError] = useState('');
    const [cleanupSuccess, setCleanupSuccess] = useState('');
    const [daysToKeep, setDaysToKeep] = useState(90);

    const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

    const fetchLogs = async () => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/admin/login-logs`, {
                params: {
                    page,
                    pageSize,
                    sortBy,
                    sortDescending,
                    searchQuery: searchQuery || null
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setLogs(response.data.logs);
            setTotalCount(response.data.totalCount);
            setTotalPages(response.data.totalPages);
        } catch (err) {
            console.error('Ошибка загрузки логов:', err);
            setError(
                err.response?.data?.message || 'Не удалось загрузить логи входов'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [page, sortBy, sortDescending]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchLogs();
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDescending(!sortDescending);
        } else {
            setSortBy(column);
            setSortDescending(true);
        }
        setPage(1);
    };

    const openCleanupModal = () => {
        setCleanupError('');
        setCleanupSuccess('');
        setDaysToKeep(90);
        setIsCleanupOpen(true);
    };

    const handleConfirmCleanup = async () => {
        setIsCleanupLoading(true);
        setCleanupError('');
        setCleanupSuccess('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_URL}/api/admin/login-logs/cleanup`, {
                params: { daysToKeep },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setCleanupSuccess(response.data.message || `Удалено записей успешно`);
            setTimeout(() => {
                setIsCleanupOpen(false);
                fetchLogs();
            }, 1500);
        } catch (err) {
            console.error('Ошибка очистки:', err);
            setCleanupError(err.response?.data?.message || 'Не удалось очистить логи');
        } finally {
            setIsCleanupLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getSortIcon = (column) => {
        const className = "inline-block w-4 h-4 ml-1 align-middle";
        if (sortBy !== column) return <ChevronsUpDown className={`${className} opacity-50`} />;
        return sortDescending ? <ChevronDown className={className} /> : <ChevronUp className={className} />;
    };

    return (
        <>
            <div className="bg-slate-800 rounded-xl p-4 sm:p-6 border border-slate-700">
                {/* Панель управления */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по имени пользователя..."
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition text-sm flex items-center gap-2"
                        >
                            <Search className="w-4 h-4" />
                            Найти
                        </button>
                    </form>
                    <button
                        onClick={openCleanupModal}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition text-sm flex items-center gap-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        Очистить старые
                    </button>
                </div>

                {/* Информация */}
                <div className="text-slate-400 text-sm mb-4">
                    Всего записей: <span className="text-white font-semibold">{totalCount}</span>
                </div>

                {/* Ошибка */}
                {error && (
                    <div className="px-3 py-2 mb-4 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Таблица */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-700 text-slate-300">
                            <tr>
                                <th
                                    className="px-3 py-2 cursor-pointer hover:bg-slate-600 select-none"
                                    onClick={() => handleSort('UserName')}
                                >
                                    Пользователь {getSortIcon('UserName')}
                                </th>
                                <th
                                    className="px-3 py-2 cursor-pointer hover:bg-slate-600 select-none"
                                    onClick={() => handleSort('LoginTime')}
                                >
                                    Время входа {getSortIcon('LoginTime')}
                                </th>
                                <th
                                    className="px-3 py-2 cursor-pointer hover:bg-slate-600 select-none"
                                    onClick={() => handleSort('LevelRight')}
                                >
                                    Уровень {getSortIcon('LevelRight')}
                                </th>
                                <th
                                    className="px-3 py-2 cursor-pointer hover:bg-slate-600 select-none"
                                    onClick={() => handleSort('GroupNum')}
                                >
                                    Группа {getSortIcon('GroupNum')}
                                </th>
                                <th
                                    className="px-3 py-2 cursor-pointer hover:bg-slate-600 select-none"
                                    onClick={() => handleSort('IpAddress')}
                                >
                                    IP адрес {getSortIcon('IpAddress')}
                                </th>
                                <th className="px-3 py-2 select-none">User Agent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-3 py-8 text-center text-slate-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="animate-spin h-5 w-5 text-blue-400" />
                                            Загрузка...
                                        </div>
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-3 py-8 text-center text-slate-400">
                                        Нет данных для отображения
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="border-b border-slate-700 hover:bg-slate-700/50 transition">
                                        <td className="px-3 py-2 text-white font-medium">
                                            {log.userName}
                                        </td>
                                        <td className="px-3 py-2 text-slate-300">
                                            {formatDate(log.loginTime)}
                                        </td>
                                        <td className="px-3 py-2 text-slate-300">
                                            {log.levelRight ?? '-'}
                                        </td>
                                        <td className="px-3 py-2 text-slate-300">
                                            {log.groupNum ?? '-'}
                                        </td>
                                        <td className="px-3 py-2 text-slate-300 font-mono text-xs">
                                            {log.ipAddress || '-'}
                                        </td>
                                        <td className="px-3 py-2 text-slate-400 text-xs max-w-xs truncate" title={log.userAgent}>
                                            {log.userAgent || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Пагинация */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                        <div className="text-slate-400 text-sm">
                            Страница {page} из {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition text-sm flex items-center gap-1"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Назад
                            </button>
                            <button
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition text-sm flex items-center gap-1"
                            >
                                Вперед
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Модальное окно очистки старых логов */}
            {isCleanupOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => !isCleanupLoading && setIsCleanupOpen(false)}
                    />
                    <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-slate-700">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-400" />
                                Очистка старых логов
                            </h2>
                            <button
                                onClick={() => !isCleanupLoading && setIsCleanupOpen(false)}
                                className="text-slate-400 hover:text-white transition p-1 rounded-lg hover:bg-slate-700 disabled:opacity-50"
                                disabled={isCleanupLoading}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {cleanupSuccess ? (
                                <div className="flex flex-col items-center py-6">
                                    <div className="w-14 h-14 rounded-full bg-green-600/20 flex items-center justify-center mb-3">
                                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                                    </div>
                                    <p className="text-green-400 font-medium text-lg">{cleanupSuccess}</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-slate-300 text-sm">
                                        Это действие удалит все записи о входах, которые были сделаны раньше указанного срока. Отменить удаление будет невозможно.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1.5">
                                            Оставлять записи за последние (дней):
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={daysToKeep}
                                            onChange={(e) => setDaysToKeep(parseInt(e.target.value) || 1)}
                                            disabled={isCleanupLoading}
                                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
                                        />
                                    </div>

                                    {cleanupError && (
                                        <div className="px-3 py-2 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            <span>{cleanupError}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex gap-3 p-5 bg-slate-800/50 border-t border-slate-700">
                            {!cleanupSuccess && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setIsCleanupOpen(false)}
                                        disabled={isCleanupLoading}
                                        className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-slate-300 hover:text-white font-medium rounded-lg transition text-sm"
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        onClick={handleConfirmCleanup}
                                        disabled={isCleanupLoading}
                                        className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition text-sm flex items-center justify-center gap-2"
                                    >
                                        {isCleanupLoading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Удаление...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 className="w-4 h-4" />
                                                Удалить
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LoginLogsTab;